import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { InferenceInstanceLoad, PlatformQueues } from "../../../api/client.ts";
import { formatNumber, humanize } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import Sparkline from "../../Sparkline.tsx";
import { StatusLabel, type StatusKind } from "../../StatusIcon.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { seconds } from "./queueFormat.ts";

const numeric = { fontVariantNumeric: "tabular-nums" } as const;

function sum(values: (number | null)[]): number {
  return values.reduce<number>((n, v) => n + (v ?? 0), 0);
}

const STATE: Record<InferenceInstanceLoad["state"], { kind: StatusKind; label: string }> = {
  serving: { kind: "ok", label: "Serving" },
  stalled: { kind: "attention", label: "Stalled" },
  offline: { kind: "error", label: "Offline" },
};

/**
 * The local inference rigs (vLLM): what each is running and has queued, its
 * KV cache and throughput, then the LLM calls per purpose and what the signal
 * engine and the classifier hand them.
 */
export function InferencePanel({ queues }: { queues: PlatformQueues }) {
  const { inference, llm, engine, classifier } = queues;
  const running = sum(inference.map((i) => i.running));
  const waiting = sum(inference.map((i) => i.waiting));

  return (
    <Panel
      title="Local inference"
      subtitle={`${formatNumber(engine.serving_instances)} of ${formatNumber(inference.length)} rigs serving · ${formatNumber(running)} running · ${formatNumber(waiting)} queued on the rigs`}
      disablePadding
      fill
    >
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Rig</TableCell>
            <TableCell>State</TableCell>
            <TableCell align="right">Running</TableCell>
            <TableCell align="right">Queued</TableCell>
            <TableCell align="right">KV cache</TableCell>
            <TableCell align="right">Tokens/s</TableCell>
            <TableCell>Load, 24h</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {inference.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} sx={{ color: "text.secondary" }}>
                No approved inference rigs.
              </TableCell>
            </TableRow>
          )}
          {inference.map((i) => {
            const state = STATE[i.state];
            return (
              <TableRow key={i.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {i.hostname}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {[i.gpu_name, i.model_name?.split("/").pop(), i.kind === "decision" ? "decision" : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <StatusLabel kind={state.kind} label={state.label} />
                  {i.state !== "serving" && (
                    <Typography variant="caption" component="div" sx={{ color: "text.secondary", pl: 3 }}>
                      Heartbeat <RelativeTime value={i.last_heartbeat_at} />
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {i.running == null ? "–" : formatNumber(i.running)}
                </TableCell>
                <TableCell align="right" sx={{ ...numeric, color: i.waiting ? "warning.main" : "text.secondary" }}>
                  {i.waiting == null ? "–" : formatNumber(i.waiting)}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {i.kv_cache_usage_pct == null ? "–" : `${Math.round(i.kv_cache_usage_pct)}%`}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  <Tooltip
                    title={
                      i.generation_tokens_1h != null
                        ? `${formatNumber(i.generation_tokens_1h)} tokens generated in the last hour · ${formatNumber(i.requests_24h)} requests in 24h`
                        : ""
                    }
                  >
                    <span>
                      {i.state === "offline" || i.tokens_per_second_24h == null
                        ? "–"
                        : formatNumber(Math.round(i.tokens_per_second_24h))}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Running plus queued, every five minutes">
                    <Box component="span">
                      <Sparkline
                        variant="area"
                        showRange={false}
                        width={96}
                        height={24}
                        values={i.trend.map((p) => (p.in_flight ?? 0) + (p.pending ?? 0))}
                      />
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </ScrollTable>

      {llm.length > 0 && (
        <ScrollTable sx={{ borderTop: 1, borderColor: "divider" }}>
          <TableHead>
            <TableRow>
              <TableCell>Model calls</TableCell>
              <TableCell align="right">Last hour</TableCell>
              <TableCell align="right">Errors</TableCell>
              <TableCell align="right">Median</TableCell>
              <TableCell align="right">p95</TableCell>
              <TableCell align="right">24h</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {llm.map((p) => (
              <TableRow key={p.purpose} hover>
                <TableCell>{humanize(p.purpose)}</TableCell>
                <TableCell align="right" sx={numeric}>
                  {formatNumber(p.calls_1h)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...numeric, color: p.errors_1h ? "error.main" : "text.secondary" }}
                >
                  {formatNumber(p.errors_1h)}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {seconds(p.p50_ms)}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {seconds(p.p95_ms)}
                </TableCell>
                <TableCell align="right" sx={{ ...numeric, color: "text.secondary" }}>
                  {formatNumber(p.calls_24h)}
                  {p.errors_24h > 0 && (
                    <Typography variant="caption" component="div" sx={{ color: "error.main" }}>
                      {formatNumber(p.errors_24h)} errors
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
      )}

      <Stack spacing={0.75} sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        <StatusLabel
          kind={engine.enabled ? "ok" : "off"}
          label={
            engine.enabled
              ? `Signal engine: ${formatNumber(engine.llm_slots)} model slots (${formatNumber(engine.llm_slots_per_instance)} per serving rig)`
              : "Signal engine paused"
          }
        />
        <StatusLabel
          kind={!classifier.enabled ? "off" : classifier.pending_is_floor ? "attention" : "ok"}
          label={
            !classifier.enabled
              ? "Classifier paused"
              : classifier.pending == null
                ? "Classifier: no backlog recorded yet"
                : `Classifier: ${classifier.pending_is_floor ? "at least " : ""}${formatNumber(classifier.pending)} companies waiting`
          }
        />
      </Stack>
    </Panel>
  );
}
